import { useCallback } from "react";
import axios from "axios";
import { useAuth } from "@clerk/react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x?.msg || String(x)).join("; ") || "Invalid input";
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return d || e?.message || "Something went wrong";
};

export function useApi() {
  const { getToken } = useAuth();
  return useCallback(
    async (method, path, data) => {
      const token = await getToken();
      const res = await axios({ method, url: `${API}${path}`, data, headers: token ? { Authorization: `Bearer ${token}` } : {} });
      return res.data;
    },
    [getToken]
  );
}

export const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const condClass = (c = "") => {
  const k = c.toLowerCase();
  if (k.startsWith("firm")) return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
  if (k.startsWith("soft")) return "bg-amber-500/15 text-amber-300 border-amber-500/30";
  if (k.startsWith("heavy")) return "bg-rose-500/15 text-rose-300 border-rose-500/30";
  return "bg-blue-500/15 text-blue-300 border-blue-500/30";
};

export const finishClass = (f) =>
  f === 1 ? "bg-amber-400 text-slate-950" : f === 2 ? "bg-slate-300 text-slate-950" : f === 3 ? "bg-orange-700 text-white" : "bg-slate-800 text-slate-300";

export const pct = (s) => (s && s.starts ? Math.round((100 * (s.wins || 0)) / s.starts) : null);

export const formLine = (starts = [], form) =>
  form || [...starts].reverse().map((s) => (s.finish == null ? "-" : s.finish >= 10 ? "0" : String(s.finish))).join("");

export const ago = (iso) => {
  if (!iso) return "";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  const when = new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Brisbane", weekday: "short", hour: "numeric", minute: "2-digit", timeZoneName: "short" });
  return `${when}${m < 60 ? ` (${m} min ago)` : m < 1440 ? ` (${Math.round(m / 60)} h ago)` : ""}`;
};

// A job still "generating" with no sign of life for 15 min (matches the server's startup rule) has been orphaned, e.g. by a crashed pod.
export const isStalled = (job) => job?.state === "generating" && Date.now() - new Date(job.updated_at || job.created_at || 0).getTime() > 15 * 60000;

export const siteToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Brisbane" });

export const nextSaturday = () => {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Brisbane" }));
  d.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const fmtDate = (d) =>
  d ? new Date(d + "T00:00:00").toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" }) : "";

export const weekendOf = (d) => {
  const dt = new Date(d + "T00:00:00");
  if (dt.getDay() === 0) dt.setDate(dt.getDate() - 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
